import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquarePlus, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SearchableSelect } from "@/components/Controlled";
import { useAuth } from "@/lib/auth";

// Correction 5 — Client Feedback & Suggestion Tool.
// Floating bottom-right button + dialog. Available to every authenticated user.
// Lightweight — does not alter any workflow.
const FALLBACK_CATEGORIES = [
  "General Feedback",
  "Suggestion",
  "Technical Issue",
  "Intake Experience",
  "Other",
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [category, setCategory] = useState("General Feedback");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load category list once. Falls back to a static list if endpoint isn't reachable.
  useEffect(() => {
    if (!user) return;
    api.get("/feedback/options")
      .then((r) => {
        if (Array.isArray(r.data?.categories) && r.data.categories.length > 0) {
          setCategories(r.data.categories);
        }
      })
      .catch(() => { /* keep fallback */ });
  }, [user]);

  // The widget only renders for authenticated users — unauthenticated landing/login don't show it.
  if (!user) return null;

  async function submit() {
    const text = (message || "").trim();
    if (!text) {
      toast.error("Please add a brief message before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        message: text,
        page_context: location.pathname || "",
      });
      toast.success("Thank you — your feedback has been recorded.");
      setMessage("");
      setCategory("General Feedback");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback or a suggestion"
        data-testid="feedback-widget-button"
        className="fixed bottom-5 right-[170px] z-40 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full shadow-lg ring-1 ring-white/15 hover:translate-y-[-1px] transition-transform"
        style={{
          background: "hsl(347 72% 22%)",
          color: "hsl(40 48% 92%)",
        }}
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="text-[12px] uppercase tracking-[0.14em] font-medium">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => !submitting && setOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle>Send feedback or a suggestion</DialogTitle>
            <DialogDescription>
              Help us refine Kandake. Bugs, questions, suggestions, and user-experience notes are all welcome.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category</Label>
              <div className="mt-1">
                <SearchableSelect
                  value={category}
                  onChange={(v) => setCategory(v || "General Feedback")}
                  options={categories}
                  placeholder="Pick a category"
                  allowClear={false}
                  testid="feedback-category-select"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="What's on your mind? Be specific where helpful — the page you're on is captured automatically."
                className="mt-1"
                data-testid="feedback-message-input"
              />
            </div>
            <div className="text-[11px] text-muted-foreground" data-testid="feedback-context-line">
              Page context: <code className="font-mono">{location.pathname || "/"}</code>
              &nbsp;·&nbsp;Submitting as <code className="font-mono">{user.email}</code>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting} data-testid="feedback-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !message.trim()}
              data-testid="feedback-submit-button"
              style={{ background: "hsl(347 72% 22%)", color: "white" }}
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
